import type { Author, MediaItem, ResolveResponse } from "./types";

export interface ThreadHtmlLabels {
  mainPost: string;
  replies: string;
  quotedPost: string;
  repliesComplete: string;
  repliesPartial: string;
  media: string;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function mediaMarkup(items: MediaItem[], label: string): string {
  if (items.length === 0) return "";
  return `<section class="media"><h3>${escapeHtml(label)} (${items.length})</h3><div class="media-grid">${items.map((item) => {
    const url = escapeHtml(item.url);
    const preview = item.type === "video"
      ? `<video controls preload="metadata"${item.thumbnailUrl ? ` poster="${escapeHtml(item.thumbnailUrl)}"` : ""} src="${url}"></video>`
      : `<img src="${url}" alt="" loading="lazy">`;
    return `<figure>${preview}<figcaption><a href="${url}" target="_blank" rel="noreferrer noopener">${escapeHtml(item.filename)}</a></figcaption></figure>`;
  }).join("")}</div></section>`;
}

function textMarkup(value: string): string {
  return value ? `<div class="body">${escapeHtml(value)}</div>` : "";
}

function authorMarkup(author: Author): string {
  const username = escapeHtml(author.username);
  const displayName = author.displayName?.trim();
  const fallback = escapeHtml((displayName || author.username || "@").slice(0, 1).toLocaleUpperCase());
  const avatar = author.avatarUrl
    ? `<img src="${escapeHtml(author.avatarUrl)}" alt="" loading="lazy">`
    : "";
  return `<div class="identity"><span class="avatar-shell" aria-hidden="true"><span class="avatar-fallback">${fallback}</span>${avatar}</span><span class="author-name"><strong>${displayName ? escapeHtml(displayName) : `@${username}`}</strong>${displayName ? `<small>@${username}</small>` : ""}</span></div>`;
}

export function buildThreadHtml(result: ResolveResponse, labels: ThreadHtmlLabels): string {
  const title = `Threads — @${result.author.username}`;
  const source = escapeHtml(result.source.canonicalUrl);
  const replyStatus = result.replySummary.complete
    ? labels.repliesComplete.replace("{count}", String(result.replySummary.loadedTotalCount))
    : labels.repliesPartial
      .replace("{loaded}", String(result.replySummary.loadedDirectCount))
      .replace("{expected}", String(result.replySummary.expectedDirectCount));
  const replies = result.replies.map((reply) => `
    <article class="reply" style="--depth:${Math.min(Math.max(reply.depth - 1, 0), 4)}">
      <div class="author">${authorMarkup(reply.author)}${reply.publishedAt ? `<time>${escapeHtml(reply.publishedAt)}</time>` : ""}</div>
      ${textMarkup(reply.text)}
      ${mediaMarkup(reply.media, labels.media)}
      <a class="permalink" href="${escapeHtml(reply.permalink)}" target="_blank" rel="noreferrer noopener">Threads ↗</a>
    </article>`).join("");
  const quoted = result.quotedPost ? `
    <section class="quoted">
      <p class="section-label">${escapeHtml(labels.quotedPost)}</p>
      <div class="author">${authorMarkup(result.quotedPost.author)}</div>
      ${textMarkup(result.quotedPost.text)}
      ${mediaMarkup(result.quotedPost.media, labels.media)}
    </section>` : "";

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta name="referrer" content="no-referrer">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; media-src https:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
  <title>${escapeHtml(title)}</title>
  <style>
    :root{color-scheme:light;--ink:#111827;--soft:#606979;--paper:#f5f1e8;--line:#d9d5cc;--blue:#4a67ff;--acid:#dfff3f}
    *{box-sizing:border-box}body{margin:0;background:var(--paper);color:var(--ink);font:16px/1.65 Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
    main{width:min(760px,calc(100% - 32px));margin:48px auto}.brand{display:flex;align-items:center;gap:10px;margin-bottom:24px;font-size:18px;font-weight:850}.mark{display:grid;place-items:center;width:34px;height:34px;border-radius:10px;background:var(--ink);color:var(--acid)}
    .card{padding:clamp(22px,5vw,42px);border:1px solid var(--line);border-radius:24px;background:#fff;box-shadow:0 20px 60px rgba(17,24,39,.08)}.section-label{margin:0 0 12px;color:#31753e;font-size:12px;font-weight:850;letter-spacing:.1em;text-transform:uppercase}.author{display:flex;align-items:center;justify-content:space-between;gap:16px}.identity{min-width:0;display:flex;align-items:center;gap:11px}.avatar-shell{position:relative;flex:0 0 auto;width:42px;height:42px;overflow:hidden;border:1px solid var(--line);border-radius:50%;background:#ebe6da}.avatar-fallback,.avatar-shell img{position:absolute;inset:0;width:100%;height:100%;border-radius:50%}.avatar-fallback{display:grid;place-items:center;color:var(--ink);font-size:14px;font-weight:850}.avatar-shell img{display:block;object-fit:cover}.author-name{min-width:0;display:grid;gap:2px;line-height:1.25}.author-name strong,.author-name small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.author-name strong{font-size:16px}.author-name small{color:var(--soft);font-size:11px}.author time{flex:0 0 auto;color:var(--soft);font-size:12px}.body{margin-top:14px;white-space:pre-wrap;overflow-wrap:anywhere}
    .source,.permalink{display:inline-block;margin-top:14px;color:var(--blue);font-size:12px;font-weight:750;text-decoration:none}.quoted{margin-top:24px;padding:18px;border-left:4px solid var(--blue);border-radius:0 14px 14px 0;background:#f3f5ff}.quoted .author{justify-content:flex-start}.quoted .avatar-shell{width:36px;height:36px}.replies{margin-top:30px;padding-top:24px;border-top:1px solid var(--line)}.reply-title{display:flex;align-items:center;justify-content:space-between;gap:16px}.reply-title h2{margin:0;font-size:18px}.status{padding:5px 9px;border-radius:999px;background:#e6f7e8;color:#286534;font-size:11px;font-weight:750}.reply{margin-top:14px;margin-left:calc(var(--depth) * 22px);padding:16px;border:1px solid var(--line);border-radius:14px;background:#fff}.reply .avatar-shell{width:34px;height:34px}.reply .avatar-fallback{font-size:11px}.reply .author-name strong{font-size:14px}.reply .body{font-size:14px}
    .media{margin-top:22px}.media h3{font-size:13px}.media-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.media figure{margin:0;overflow:hidden;border:1px solid var(--line);border-radius:12px;background:#f2f3f5}.media img,.media video{display:block;width:100%;aspect-ratio:1/1;object-fit:cover}.media figcaption{padding:9px}.media figcaption a{display:block;overflow:hidden;color:var(--ink);font-size:11px;text-overflow:ellipsis;white-space:nowrap}
    footer{margin-top:20px;color:var(--soft);font-size:11px;text-align:center}@media(max-width:560px){main{margin:20px auto}.card{border-radius:18px}.reply-title{align-items:flex-start;flex-direction:column}.media-grid{grid-template-columns:1fr}.reply{margin-left:calc(var(--depth) * 10px)}}
  </style>
</head>
<body>
  <main>
    <div class="brand"><span class="mark">▰</span>SiloSave</div>
    <article class="card">
      <p class="section-label">${escapeHtml(labels.mainPost)}</p>
      <div class="author">${authorMarkup(result.author)}${result.publishedAt ? `<time>${escapeHtml(result.publishedAt)}</time>` : ""}</div>
      ${textMarkup(result.text)}
      ${mediaMarkup(result.media, labels.media)}
      <a class="source" href="${source}" target="_blank" rel="noreferrer noopener">Threads ↗</a>
      ${quoted}
      ${(result.replies.length > 0 || result.replySummary.expectedDirectCount > 0) ? `<section class="replies"><div class="reply-title"><h2>${escapeHtml(labels.replies)} (${result.replySummary.loadedTotalCount})</h2><span class="status">${escapeHtml(replyStatus)}</span></div>${replies}</section>` : ""}
    </article>
    <footer>Saved with SiloSave · ${escapeHtml(result.resolvedAt)}</footer>
  </main>
</body>
</html>`;
}
